import React, { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DeleteIcon from '@mui/icons-material/Delete';
import { Link, useParams } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { canAccessSurveyResponses, canDeleteResponses } from '../../constants/surveyAccessPermissions';
import { useSurveyRuntimeApi } from '../context/SurveyRuntimeApiContext';
import { getApiErrorMessage } from '../api/runtimeErrors';
import { formatAnswerValue, formatResponseLocation } from '../utils/responseFormat';
import { exportSurveyResponsesToExcel } from '../utils/exportResponsesToExcel';
import { useSurveyUiToast } from '../hooks/useSurveyUiToast';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip, IconButton } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import CloseIcon from '@mui/icons-material/Close';

const EXPORT_LIMIT = 500;

export default function SurveyResponsesPage() {
  const { apiFetch } = useSurveyRuntimeApi();
  const { id } = useParams();
  const { hasPermission } = useTenant();
  const { showError, showSuccess } = useSurveyUiToast();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const canView = canAccessSurveyResponses(hasPermission);
  const canDelete = canDeleteResponses(hasPermission);

  const loadResponses = useCallback(async () => {
    const res = await apiFetch(`/admin/surveys/${id}/responses`);
    setData(res);
    return res;
  }, [apiFetch, id]);

  useEffect(() => {
    if (!id || !canView) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await loadResponses();
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) setErr(getApiErrorMessage(e, 'Failed to load responses'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, canView, loadResponses]);

  const handleExportExcel = async () => {
    if (!data || data.total_count === 0) return;

    setExporting(true);
    try {
      let exportPayload = data;
      if (data.truncated) {
        exportPayload = await apiFetch(
          `/admin/surveys/${id}/responses?limit=${EXPORT_LIMIT}`,
        );
      }
      const exported = exportSurveyResponsesToExcel(exportPayload);
      if (!exported) {
        showError('No responses to export.');
      }
    } catch (e) {
      showError(getApiErrorMessage(e, 'Failed to export responses to Excel'));
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteResponse = async (responseId) => {
    setDeleting(true);
    try {
      await apiFetch(`/admin/responses/${responseId}`, {
        method: 'DELETE',
      });
      setDeleteConfirm(null);
      await new Promise(r => setTimeout(r, 500));
      await loadResponses();
      showSuccess('Response deleted successfully. User can now retake the survey.');
    } catch (e) {
      console.error('Delete error:', e);
      setDeleteConfirm(null);
      showError(getApiErrorMessage(e, 'Failed to delete response'));
    } finally {
      setDeleting(false);
    }
  };

  if (!canView) {
    return (
      <div className="page">
        <div className="alert error">You do not have permission to view response details.</div>
        <Link className="btn secondary" to="/survey/manage">
          Back to surveys
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading responses…
        </Typography>
      </Box>
    );
  }

  if (err || !data) {
    return (
      <div className="page">
        <div className="alert error">{err ?? 'No data'}</div>
        <Link className="btn secondary" to="/survey/manage">
          Back to surveys
        </Link>
      </div>
    );
  }

  const canExport = data.total_count > 0;

  return (
    <div className="page survey-responses-page">
      <header className="page-header">
        <div className="survey-responses-page-top">
          <div>
            <h1>Responses</h1>
            <p className="lede">
              <strong>{data.survey_title}</strong>
              {data.total_count > 0 && (
                <>
                  {' '}
                  · {data.total_count} submission{data.total_count === 1 ? '' : 's'}
                  {data.truncated && (
                    <span className="muted"> (showing the most recent {data.responses.length})</span>
                  )}
                </>
              )}
            </p>
            <p className="lede" style={{ marginTop: '-1rem', marginBottom: 0 }}>
              <Link to="/survey/responses">← All surveys with responses</Link>
              {' · '}
              <Link to={`/survey/${id}/edit`}>Edit survey</Link>
            </p>
          </div>
          {canExport && (
            <button
              type="button"
              className="btn primary survey-responses-export-btn"
              onClick={() => void handleExportExcel()}
              disabled={exporting}
            >
              <FileDownloadIcon className="survey-responses-export-icon" />
              {exporting ? 'Exporting…' : 'Export to Excel'}
            </button>
          )}
        </div>
      </header>

      {data.total_count === 0 && (
        <div className="card survey-responses-hub-empty">
          <p className="muted" style={{ margin: 0 }}>
            No responses have been submitted for this survey yet.
          </p>
        </div>
      )}

      <div className="survey-responses-list">
        {data.responses.map((r) => (
          <article key={r.id} className="card survey-response-card">
            <div className="survey-response-card-head">
              <span className="badge ghost">Response #{r.id}</span>
              {formatResponseLocation(r.location) && (
                <span className="badge ghost survey-response-location">
                  {formatResponseLocation(r.location)}
                </span>
              )}
              <span className="muted small">
                {new Date(r.submitted_at_utc).toLocaleString()} · version {r.survey_version_id}
              </span>
              {canDelete && (
                <IconButton
                  size="small"
                  onClick={() => setDeleteConfirm(r)}
                  sx={{ ml: 'auto' }}
                  title="Delete response"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </div>
            <dl className="survey-response-dl">
              {r.answers.map((a) => (
                <div key={a.question_key} className="survey-response-row">
                  <dt>{a.question_label}</dt>
                  <dd>{formatAnswerValue(a) || '—'}</dd>
                </div>
              ))}
            </dl>
          </article>
        ))}
      </div>

      <Dialog open={!!deleteConfirm} onClose={() => !deleting && setDeleteConfirm(null)}>
        <DialogTitle>Delete Response</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete response #{deleteConfirm?.id}?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This will allow the user to retake the survey if it's a secure survey.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={() => handleDeleteResponse(deleteConfirm.id)}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
